/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { EuiFlexGroup, EuiIconTip, EuiLoadingSpinner, EuiText } from '@elastic/eui'

import useGetBillingCloudResources from '@modules/billing-lib/queries/useGetBillingCloudResources'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

const TextWithEllipsis: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
  <EuiText
    css={css({
      maxWidth: 232,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    })}
    size='s'
  >
    {children}
  </EuiText>
)

type Props = {
  scopeValue: string
}

const CloudResourceItem: FunctionComponent<Props> = ({ scopeValue }) => {
  const { formatMessage } = useIntl()
  const organizationId = useOrganizationId()

  const {
    data: billingCloudResources,
    isLoading,
    isError,
  } = useGetBillingCloudResources(organizationId)

  if (isLoading) {
    return (
      <EuiFlexGroup gutterSize='xs' responsive={false} alignItems='center'>
        <TextWithEllipsis>{scopeValue}</TextWithEllipsis>
        <EuiLoadingSpinner size='s' />
      </EuiFlexGroup>
    )
  }

  const matchedResource = billingCloudResources?.find((resource) => resource.id === scopeValue)

  if (isError) {
    return <TextWithEllipsis>{scopeValue}</TextWithEllipsis>
  }

  if (!matchedResource) {
    return (
      <EuiFlexGroup gutterSize='xs' responsive={false} alignItems='center'>
        <EuiIconTip
          aria-label={formatMessage({
            id: 'billing.notifications.budgets.cloud-resource-item.deleted-resource.aria-label',
            defaultMessage: 'Warning: selected cloud resource has been removed',
          })}
          size='s'
          type='warning'
          color='warning'
          content={formatMessage({
            id: 'billing.notifications.budgets.cloud-resource-item.deleted-resource.tooltip',
            defaultMessage: 'Selected cloud resource has been removed.',
          })}
        />
        <TextWithEllipsis>{scopeValue}</TextWithEllipsis>
      </EuiFlexGroup>
    )
  }

  return <TextWithEllipsis>{matchedResource.name}</TextWithEllipsis>
}

export default CloudResourceItem
